import { useMemo, useState } from "react";
import {
  buildBoard,
  buildGameMarkets,
  contestTeams,
  pickMainLine,
  type Book,
  type MarketGroup,
  type PriceMode,
  type Teams,
} from "../lib/board";
import { cn } from "../lib/cn";
import { fmtStart } from "../lib/format";
import type { Contest, MarketDef, Partner, Position } from "../lib/types";
import { ChipRow } from "./ChipRow";
import { PriceHistory } from "./PriceHistory";
import { BookComparison, ComparisonNotes, lineChipLabel } from "./VenuePanel";

/**
 * One game in full, as a set of selectors rather than a long scroll: pick a MARKET, then (for
 * props and other per-participant markets) a PLAYER or selection, then a LINE — and the venue
 * comparison for exactly that shows underneath. Game markets and player props are split by the
 * API's own category.
 */
export function GameDetail({
  contest,
  league,
  positions,
  loading,
  markets,
  marketsReady,
  mode,
  partners,
  onBack,
}: {
  contest: Contest;
  league?: string;
  positions: Position[] | undefined;
  loading: boolean;
  markets: MarketDef[] | undefined;
  /** The market definitions have loaded (or failed — then everything files under Game). */
  marketsReady: boolean;
  mode: PriceMode;
  partners?: Map<string, Partner>;
  onBack: () => void;
}) {
  const [tab, setTab] = useState<"game" | "player">("game");
  const [marketKey, setMarketKey] = useState<string | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [bookId, setBookId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const all = useMemo(
    () => (positions && marketsReady ? buildGameMarkets(positions, markets, mode) : null),
    [positions, markets, marketsReady, mode],
  );
  const teams = useMemo(() => gameTeams(all?.game, contest), [all, contest]);
  const board = useMemo(() => (positions ? buildBoard(positions, mode) : null), [positions, mode]);
  const s = fmtStart(contest.starts_at);
  const title = teams.away && teams.home ? `${teams.away} @ ${teams.home}` : (contest.title ?? "Game");

  const groups = all ? (tab === "game" ? all.game : all.player) : [];
  const market = groups.find((g) => g.key === marketKey) ?? groups[0] ?? null;
  const participants = useMemo(() => (market?.multi ? participantsOf(market.books) : []), [market]);
  const participant = market?.multi
    ? (participants.find((p) => p.id === participantId) ?? participants[0] ?? null)
    : null;
  const candidates = market
    ? market.multi
      ? market.books.filter((b) => participantKey(b) === participant?.id)
      : market.books
    : [];
  const main = pickMainLine(candidates);
  const book = candidates.find((b) => b.id === bookId) ?? main;

  const pickTab = (next: "game" | "player") => {
    setTab(next);
    setMarketKey(null);
    setParticipantId(null);
    setBookId(null);
    setFilter("");
  };
  const pickMarket = (key: string) => {
    setMarketKey(key);
    setParticipantId(null);
    setBookId(null);
    setFilter("");
  };
  const pickParticipant = (id: string) => {
    setParticipantId(id);
    setBookId(null);
  };

  const needle = filter.trim().toLowerCase();
  const shownParticipants = needle
    ? participants.filter((p) => p.id === participant?.id || p.name.toLowerCase().includes(needle))
    : participants;
  const playerCount = all?.player.length ?? 0;

  return (
    <div className="pb-16">
      <button
        type="button"
        onClick={onBack}
        className="mt-4 inline-flex items-center gap-1.5 rounded-full px-1 text-[13px] font-semibold text-dim transition-colors hover:text-ink"
      >
        <span aria-hidden>←</span> Back to the board
      </button>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">
            {[league, s.live ? "Live" : [s.day ?? "Today", s.time].join(" · ")].filter(Boolean).join(" · ")}
          </div>
          <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
        </div>
        <div className="flex gap-1 rounded-full border border-line bg-panel/70 p-1">
          <Pill active={tab === "game"} onClick={() => pickTab("game")}>
            Game <Count n={all?.game.length ?? 0} active={tab === "game"} />
          </Pill>
          <Pill active={tab === "player"} onClick={() => pickTab("player")} disabled={all !== null && playerCount === 0}>
            Player props <Count n={playerCount} active={tab === "player"} />
          </Pill>
        </div>
      </div>

      {board && (
        <PriceHistory
          contest={contest}
          board={board}
          title={title}
          league={league}
          teams={[teams.away, teams.home].filter((t): t is string => !!t)}
        />
      )}

      {loading || !all ? (
        <div className="skeleton mt-5 h-96 rounded-2xl" />
      ) : !market ? (
        <div className="mt-5 rounded-2xl border border-line bg-card/60 px-6 py-14 text-center text-[13px] text-dim">
          {tab === "player" ? "No venue is quoting player props on this game right now." : "Nothing is quoted on this game right now."}
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-5 rounded-2xl border border-line bg-card/80 p-4 backdrop-blur-sm sm:p-5">
          <ChipRow
            label="Market"
            chips={groups.map((g) => ({ id: g.key, label: g.label }))}
            value={market.key}
            onChange={pickMarket}
          />

          {market.multi && participant && (
            <ChipRow
              label={tab === "player" ? "Player" : "Selection"}
              chips={shownParticipants.map((p) => ({
                id: p.id,
                label: p.name,
                hint: market.hasLines && p.lines > 1 ? String(p.lines) : undefined,
              }))}
              value={participant.id}
              onChange={pickParticipant}
              search={
                participants.length > 10
                  ? { value: filter, onChange: setFilter, placeholder: tab === "player" ? "Find a player" : "Find" }
                  : null
              }
            />
          )}

          {market.hasLines && candidates.length > 1 && book && (
            <ChipRow
              label={lineCaption(market, teams)}
              chips={candidates.map((b) => ({ id: b.id, label: lineChipLabel(b), marked: b.id === main?.id }))}
              value={book.id}
              onChange={setBookId}
              action={main && book.id !== main.id ? { label: "Main line", onClick: () => setBookId(main.id) } : null}
            />
          )}

          <div className="border-t border-line pt-5">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-display text-lg font-bold">
                {participant ? `${participant.name} · ` : ""}
                {market.label}
                {book && market.hasLines ? ` · ${lineChipLabel(book)}` : ""}
              </span>
              <span className="text-[12px] text-dim">
                {venueCount(book)} betting {venueCount(book) === 1 ? "app" : "apps"} offering odds
              </span>
            </div>
            <BookComparison book={book} partners={partners} />
          </div>

          <ComparisonNotes />
        </div>
      )}
    </div>
  );
}

/** Players (or teams, or outcomes) in a per-participant market, with how many lines each has. */
interface Participant {
  id: string;
  name: string;
  lines: number;
}

function participantKey(book: Book): string {
  return book.participantId ?? book.participantName ?? "";
}

function participantsOf(books: Book[]): Participant[] {
  const byId = new Map<string, Participant>();
  for (const b of books) {
    const id = participantKey(b);
    const existing = byId.get(id);
    if (existing) existing.lines++;
    else byId.set(id, { id, name: b.participantName ?? "—", lines: 1 });
  }
  return [...byId.values()];
}

function venueCount(book: Book | null): number {
  return book ? new Set(book.sides.flatMap((s) => s.quotes.map((q) => q.partnerId))).size : 0;
}

/** Spreads name the team the chip line belongs to; everything else is just "Line". */
function lineCaption(market: MarketGroup, teams: Teams): string {
  const awaySide = market.books.some((b) => b.sides.some((s) => s.sideKey === "away"));
  return awaySide && teams.away ? `Line · ${teams.away}` : "Line";
}

/** Team names: from the moneyline if it's quoted, else the contest. */
function gameTeams(game: MarketGroup[] | undefined, contest: Contest): Teams {
  const ml = game?.find((g) => g.key === "moneyline")?.books[0];
  const fallback = contestTeams(contest);
  return {
    away: ml?.sides.find((s) => s.sideKey === "away")?.name ?? fallback.away,
    home: ml?.sides.find((s) => s.sideKey === "home")?.name ?? fallback.home,
  };
}

function Pill({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all disabled:opacity-40",
        active ? "brand-gradient text-accent-ink" : "text-dim hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function Count({ n, active }: { n: number; active: boolean }) {
  return <span className={cn("text-[11px]", active ? "opacity-70" : "text-faint")}>{n}</span>;
}
